/**
 * Log in. Email + password; falls back to the verification banner on the
 * home screen if the user hasn't verified yet — cash flows are gated
 * server-side either way.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { bindSubmit, escape, field, setStatus, statusZone } from '../components/form.js';
import { toast } from '../components/toast.js';
import { resumePendingInvite } from '../lib/pending_invite.js';
import {
  exchangeSocialToken,
  loadSocialAuthConfig,
  prefetchSocialScripts,
  renderGoogleButton,
  signInWithFacebook,
} from '../lib/social_auth.js';
import { navigate } from '../router.js';

const STATUS_ID = 'login-status';

export function renderLogin(root) {
  // Start the GSI script download immediately, in parallel with the
  // /auth/config fetch and the rest of this render. By the time the
  // social-buttons section actually paints below the form, the script
  // is already in cache.
  prefetchSocialScripts();

  root.innerHTML = `
    <div class="appbar">
      <a href="/onboarding" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Log in</div>
    </div>

    <div class="pad">
      <form id="login-form" class="form card" novalidate>
        ${field({ name: 'email', label: 'Email', type: 'email', autocomplete: 'email', placeholder: 'you@example.com' })}
        ${field({ name: 'password', label: 'Password', type: 'password', autocomplete: 'current-password', placeholder: '••••••••' })}
        ${statusZone(STATUS_ID)}
        <button type="submit" class="btn">Log in</button>
      </form>

      <div id="social-buttons"></div>

      <div class="row" style="justify-content:space-between;margin-top:14px;font-size:12.5px;">
        <a href="/forgot-password" data-link>Forgot password?</a>
        <a href="/signup" data-link>Create an account</a>
      </div>
    </div>
  `;

  mountSocialButtons(document.getElementById('social-buttons'), 'signin');

  const form = document.getElementById('login-form');
  bindSubmit(form, async (data) => {
    setStatus(STATUS_ID, { message: 'Signing you in…', kind: 'info' });
    try {
      const tokens = await api('/auth/login', {
        method: 'POST',
        body: { email: data.email, password: data.password },
      });
      auth.setSession(tokens);
      const me = await api('/me');
      auth.setMe(me);

      // If they landed on /login from a /play/invite/:code link, resume that
      // join now that we have a token — sends them straight into the match.
      if (await resumePendingInvite()) return;

      toast.success(`Welcome back${me.username ? ', ' + me.username : ''}.`);
      navigate('/');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'account_banned') {
        renderBanNotice(err);
        return;
      }
      const msg = err instanceof ApiError ? err.message : 'Could not log in.';
      setStatus(STATUS_ID, { message: msg, kind: 'error' });
    }
  });
}

// Replace the login form with a solid ban-notice panel. Keyed off
// `code=account_banned` from /auth/login; the admin-authored message
// (if any) rides in `details.admin_message`. Rendering a distinct
// screen instead of a red toast because banned users routinely try
// again — a persistent panel makes the state unambiguous.
function renderBanNotice(err) {
  const adminMessage = (err?.details?.admin_message || '').trim();
  const host = document.getElementById('auth-shell') || document.querySelector('#app') || document.body;
  const shell = host.querySelector('#login-form')?.closest('.card') || host;
  const adminBlock = adminMessage
    ? `<div style="margin-top:14px;padding:12px 14px;background:rgba(0,0,0,.25);border-left:3px solid #F87171;border-radius:8px;font-size:13.5px;line-height:1.55;color:#FEE2E2;white-space:pre-wrap;">${escape(adminMessage)}</div>`
    : '';
  host.innerHTML = `
    <div class="card" style="max-width:440px;margin:24px auto;padding:24px;background:linear-gradient(135deg,#7F1D1D,#B91C1C);border:1px solid rgba(248,113,113,.5);color:#FEE2E2;">
      <div style="display:flex;align-items:center;gap:10px;">
        <div style="font-size:26px;line-height:1;">⛔</div>
        <div style="font-weight:800;font-size:18px;color:#FFF;">Account banned</div>
      </div>
      <div style="margin-top:10px;font-size:14px;line-height:1.55;">
        This account has been banned and can no longer log in.
      </div>
      ${adminBlock}
      <div style="margin-top:18px;font-size:12.5px;color:#FCA5A5;line-height:1.55;">
        If you believe this is a mistake, reply to the ban email that was
        sent to your inbox — our team will review.
      </div>
      <div style="margin-top:18px;">
        <a href="/login" data-link class="btn ghost" style="width:100%;text-align:center;">Back to login</a>
      </div>
    </div>
  `;
  // Ensure shell reference doesn't hold a stale form binding — harmless
  // when unused since we replaced the container.
  void shell;
}

/**
 * Mount the social sign-in buttons. `mode` is "signin" or "signup" — only
 * affects the Google button label ("Sign in with Google" vs "Sign up with
 * Google"). The post-login behaviour is identical: new users are auto-
 * created server-side, existing users just sign back in.
 */
async function mountSocialButtons(container, mode) {
  if (!container) return;
  const config = await loadSocialAuthConfig();
  const hasGoogle = !!config.google_client_id;
  const hasFacebook = !!config.facebook_app_id;
  if (!hasGoogle && !hasFacebook) {
    // Nothing to render — keep the form looking clean.
    container.style.display = 'none';
    return;
  }
  // Divider appears ABOVE the social card now (form is above us). The
  // "or use email" copy doesn't fit the new flow — flip it to lead the
  // user from the email form INTO the social options.
  container.innerHTML = `
    <div class="row center" style="margin:14px 0 10px;font-size:11px;color:var(--mute);">
      <span style="flex:1;border-top:1px solid var(--line);"></span>
      <span style="padding:0 9px;">or continue with</span>
      <span style="flex:1;border-top:1px solid var(--line);"></span>
    </div>
    <div class="card" style="padding:14px;">
      <div id="google-btn-host" style="display:flex;justify-content:center;margin-bottom:${hasFacebook ? '8' : '0'}px;"></div>
      ${hasFacebook
        ? '<button type="button" id="facebook-btn" class="btn" style="background:#1877F2;color:#fff;width:100%;">Continue with Facebook</button>'
        : ''}
    </div>
  `;

  if (hasGoogle) {
    renderGoogleButton(document.getElementById('google-btn-host'), {
      clientId: config.google_client_id,
      label: mode === 'signup' ? 'signup_with' : 'signin_with',
      onCredential: (idToken) => handleSocialAuth({ provider: 'google', token: idToken, mode }),
    }).catch((err) => {
      console.error('google sign-in init', err);
    });
  }
  if (hasFacebook) {
    document.getElementById('facebook-btn').addEventListener('click', async () => {
      try {
        const token = await signInWithFacebook({ appId: config.facebook_app_id });
        if (!token) return;
        await handleSocialAuth({ provider: 'facebook', token, mode });
      } catch (err) {
        toast.error(err?.message || 'Facebook sign-in failed.');
      }
    });
  }
}

async function handleSocialAuth({ provider, token, mode }) {
  setStatus(STATUS_ID, { message: 'Signing you in…', kind: 'info' });
  try {
    const tokens = await exchangeSocialToken({ provider, token });
    auth.setSession(tokens);
    const me = await api('/me');
    auth.setMe(me);
    if (await resumePendingInvite()) return;
    toast.success(`Welcome${me.username ? ', ' + me.username : ''}.`);
    // Brand-new accounts (no interests yet) get the topic-picker; returning
    // users go straight home.
    navigate(mode === 'signup' && !me.username ? '/interests' : '/');
  } catch (err) {
    if (err instanceof ApiError && err.code === 'account_banned') {
      renderBanNotice(err);
      return;
    }
    const msg = err instanceof ApiError ? err.message : 'Social sign-in failed.';
    setStatus(STATUS_ID, { message: msg, kind: 'error' });
  }
}
